<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-shield-key-outline me-2"></i>{{ $t('page.sha', 'SHA Integration') }}</h4>
          <span class="hms-env-pill" :class="'env-' + (env || 'uat')">{{ (env || 'uat').toUpperCase() }}</span>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-blue" @click="$router.push('/insurance')"><i class="mdi mdi-shield-check-outline me-1"></i>Claims</button>
          <button class="hms-btn hms-btn-blue" @click="$router.push('/settings')"><i class="mdi mdi-cog-outline me-1"></i>SHA Settings</button>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab==='preauth' }"  @click="tab='preauth'"><i class="mdi mdi-clipboard-check-outline me-1"></i>Pre-Authorisations</button>
        <button class="hms-tab" :class="{ active: tab==='verify' }"   @click="tab='verify'"><i class="mdi mdi-account-search me-1"></i>Verifications</button>
        <button class="hms-tab" :class="{ active: tab==='webhook' }"  @click="tab='webhook'"><i class="mdi mdi-webhook me-1"></i>Webhook</button>
      </div>

      <!-- ============ PRE-AUTH ============ -->
      <div v-if="tab === 'preauth'">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-primary" @click="openCreate"><i class="mdi mdi-plus me-1"></i>New Pre-Auth</button>
          </div>
          <select class="hms-input hms-filter" v-model="preauthStatus" @change="loadPreauths">
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="queued">Queued</option>
            <option value="in_review">In Review</option>
            <option value="clinical_review">Clinical Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="used">Used</option>
            <option value="expired">Expired</option>
          </select>
        </div>

        <div v-if="loading && !preauths.length" class="hms-spinner"><div class="spinner-border"></div></div>
        <div v-else-if="preauths.length" class="hms-card" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr><th>Pre-Auth #</th><th>Patient</th><th>SHA #</th><th>Service</th><th>Diagnosis</th><th>Amount</th><th>Status</th><th>SHA Token</th><th></th></tr></thead>
            <tbody>
              <tr v-for="p in preauths" :key="p.preauth_id" @click="openDetail(p.preauth_id)" class="hms-row-click">
                <td><span class="hms-mono fw-semibold">{{ p.preauth_number }}</span></td>
                <td>
                  <div class="fw-semibold">{{ p.patient_name }}</div>
                  <div class="hms-muted-sm hms-mono">{{ p.patient_no }}</div>
                </td>
                <td class="hms-mono">{{ p.insurance_number || '—' }}</td>
                <td>
                  <div class="fw-semibold">{{ p.intervention_name }}</div>
                  <div class="hms-muted-sm hms-mono">{{ p.intervention_code }}</div>
                </td>
                <td>
                  <div>{{ p.icd11_display }}</div>
                  <div class="hms-muted-sm hms-mono">{{ p.icd11_code }}</div>
                </td>
                <td>
                  <strong>KSh {{ formatNum(p.requested_amount) }}</strong>
                  <div class="hms-muted-sm" v-if="p.approved_amount > 0">Apv {{ formatNum(p.approved_amount) }}</div>
                </td>
                <td><span class="hms-badge" :class="statusBadge(p.status)">{{ formatStatus(p.status) }}</span></td>
                <td class="hms-mono">{{ p.preauth_token || '—' }}</td>
                <td><i class="mdi mdi-chevron-right text-muted"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-card hms-empty"><i class="mdi mdi-clipboard-check-outline"></i>No pre-authorisations yet.</div>
      </div>

      <!-- ============ VERIFICATIONS ============ -->
      <div v-if="tab === 'verify'" class="row g-3">
        <!-- Facility verify -->
        <div class="col-md-6">
          <div class="hms-card h-100">
            <div class="hms-card-title"><i class="mdi mdi-hospital-building me-1"></i>Verify Facility</div>
            <div class="hms-info-banner mb-2"><i class="mdi mdi-information-outline me-1"></i>Confirms the SHA Facility ID is registered. Run after Branches admin assigns FIDs.</div>
            <label class="hms-label">SHA FID</label>
            <input class="hms-input mb-2" v-model="verifyFid" placeholder="FID-33-100001-1" />
            <button class="hms-btn hms-btn-primary" @click="doVerifyFacility" :disabled="verifying || !verifyFid">
              <span v-if="verifying === 'fac'" class="spinner-border spinner-border-sm me-1"></span>
              Verify Facility
            </button>
            <div v-if="facResult" class="hms-result mt-2" :class="facResult.success ? 'ok' : 'err'">
              <i class="mdi" :class="facResult.success ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline'"></i>
              {{ facResult.message }}
              <pre v-if="facResult.response" class="hms-pre-json mt-2">{{ JSON.stringify(facResult.response, null, 2) }}</pre>
            </div>
          </div>
        </div>

        <!-- Practitioner verify -->
        <div class="col-md-6">
          <div class="hms-card h-100">
            <div class="hms-card-title"><i class="mdi mdi-doctor me-1"></i>Verify Practitioner</div>
            <div class="hms-info-banner mb-2"><i class="mdi mdi-information-outline me-1"></i>Confirms the doctor's SHA PUID is registered. Required before claims that reference this practitioner.</div>
            <label class="hms-label">PUID</label>
            <input class="hms-input mb-2" v-model="verifyPuid" placeholder="PUID-0000002-1" />
            <button class="hms-btn hms-btn-primary" @click="doVerifyPractitioner" :disabled="verifying || !verifyPuid">
              <span v-if="verifying === 'prac'" class="spinner-border spinner-border-sm me-1"></span>
              Verify Practitioner
            </button>
            <div v-if="pracResult" class="hms-result mt-2" :class="pracResult.success ? 'ok' : 'err'">
              <i class="mdi" :class="pracResult.success ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline'"></i>
              {{ pracResult.message }}
              <pre v-if="pracResult.response" class="hms-pre-json mt-2">{{ JSON.stringify(pracResult.response, null, 2) }}</pre>
            </div>
          </div>
        </div>

        <!-- Member verify -->
        <div class="col-12">
          <div class="hms-card">
            <div class="hms-card-title"><i class="mdi mdi-account-check-outline me-1"></i>Verify Member Eligibility</div>
            <div class="hms-info-banner mb-2"><i class="mdi mdi-information-outline me-1"></i>Calls the SHA Client Registry to confirm a beneficiary is active before service. If verified, the SHA number is saved on the patient record.</div>
            <div class="row g-3">
              <div class="col-md-4"><label class="hms-label">Patient (typeahead)</label>
                <input class="hms-input" v-model="memberSearch" placeholder="Search by name / phone..." @input="debouncedPatientSearch" />
                <div v-if="patientResults.length" class="hms-search-results">
                  <div v-for="p in patientResults" :key="p.patient_id" class="hms-search-result" @click="pickMember(p)">
                    <div class="fw-semibold">{{ p.first_name }} {{ p.last_name }}</div>
                    <div class="hms-muted-sm">{{ p.patient_no }} · {{ p.phone || '—' }}</div>
                  </div>
                </div>
                <div v-if="memberPatient" class="hms-muted-sm mt-1"><i class="mdi mdi-check"></i> {{ memberPatient.first_name }} {{ memberPatient.last_name }}</div>
              </div>
              <div class="col-md-4"><label class="hms-label">SHA Number</label>
                <input class="hms-input" v-model="verifyShaNo" placeholder="SHA1836543210987-4" />
              </div>
              <div class="col-md-4"><label class="hms-label">CR ID (alt)</label>
                <input class="hms-input" v-model="verifyCrId" placeholder="CR1234567890123-4" />
              </div>
            </div>
            <button class="hms-btn hms-btn-primary mt-2" @click="doVerifyMember" :disabled="verifying">
              <span v-if="verifying === 'mem'" class="spinner-border spinner-border-sm me-1"></span>
              Verify Member
            </button>
            <div v-if="memResult" class="hms-result mt-2" :class="memResult.success ? 'ok' : 'err'">
              <i class="mdi" :class="memResult.success ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline'"></i>
              {{ memResult.message }}
              <div v-if="memResult.success" class="hms-muted-sm mt-1">SHA #: <strong>{{ memResult.sha_number || '—' }}</strong> · CR ID: <strong>{{ memResult.cr_id || '—' }}</strong> · {{ memResult.patient_name }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ WEBHOOK ============ -->
      <div v-if="tab === 'webhook'" class="hms-card">
        <div class="hms-card-title"><i class="mdi mdi-webhook me-1"></i>Inbound Webhook (SHA → HMS)</div>
        <div class="hms-info-banner mb-3">
          <i class="mdi mdi-information-outline me-1"></i>
          SHA pushes status updates to a callback URL with HTTP Basic auth. Configure these three keys in Settings → Advanced and share the URL with the SHA technical team.
        </div>
        <table class="hms-kv-table">
          <tr><th>Endpoint URL</th><td><code>{{ apiBase }}?function=shaWebhook&hospital_id={{ hospitalId }}</code></td></tr>
          <tr><th>Method</th><td>POST</td></tr>
          <tr><th>Content-Type</th><td><code>application/json</code></td></tr>
          <tr><th>Auth</th><td>HTTP Basic — username + password from <code>sha_callback_user</code> + <code>sha_callback_pass</code></td></tr>
          <tr><th>Expected Body</th><td>One claim status object or array of objects (per SHA spec): <code>{ "claim_id", "claim_status", "response": { "approved_amount", "preauth_token" }, "errors": [] }</code></td></tr>
          <tr><th>What it does</th><td>Updates the matching <code>insurance_claims</code> + <code>insurance_preauth</code> rows by <code>claim_uuid</code> or <code>mediator_id</code>; maps SHA status into our enum; records approved amount / preauth token / rejection reason; writes audit log.</td></tr>
        </table>

        <div class="mt-3">
          <div class="hms-card-title small"><i class="mdi mdi-test-tube me-1"></i>Test the webhook</div>
          <p class="hms-muted-sm">Sample cURL to simulate a SHA callback (replace user:pass with your sha_callback_user / sha_callback_pass):</p>
          <pre class="hms-pre-json">curl -X POST '{{ apiBase }}?function=shaWebhook&hospital_id={{ hospitalId }}' \
  -u 'YOUR_USER:YOUR_PASS' \
  -H 'Content-Type: application/json' \
  -d '{"claim_id":"PASTE_CLAIM_UUID","claim_status":"approved","response":{"approved_amount":1130,"preauth_token":"6UN77NJV9X"}}'</pre>
        </div>
      </div>

      <!-- ============ CREATE PRE-AUTH MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showCreate" @click.self="showCreate=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-plus me-1"></i>New SHA Pre-Authorisation</span>
            <button class="hms-modal-close" @click="showCreate=false">&times;</button>
          </div>
          <div v-if="createError" class="hms-alert hms-alert-danger">{{ createError }}</div>

          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Patient *</label>
              <input class="hms-input" v-model="patientSearch" placeholder="Search name / phone / SHA #..." @input="debouncedPatientSearch" />
              <div v-if="patientResults.length" class="hms-search-results">
                <div v-for="p in patientResults" :key="p.patient_id" class="hms-search-result" @click="pickPatient(p)">
                  <div class="fw-semibold">{{ p.first_name }} {{ p.last_name }}</div>
                  <div class="hms-muted-sm">{{ p.patient_no }} · {{ p.phone || '—' }} · SHA: {{ p.sha_number || '—' }}</div>
                </div>
              </div>
              <div v-if="newPre.patient_id" class="hms-muted-sm mt-1"><i class="mdi mdi-check"></i> {{ pickedLabel }}</div>
            </div>
            <div class="col-md-6"><label class="hms-label">Visit (optional)</label>
              <input class="hms-input" v-model="newPre.visit_id" placeholder="Visit ID" />
            </div>
            <div class="col-12"><label class="hms-label">SHA Intervention Code *</label>
              <select class="hms-input" v-model="selectedCode" @change="onCodeChange">
                <option value="">— Select intervention —</option>
                <optgroup v-for="grp in groupedCodes" :key="grp.cat" :label="grp.cat">
                  <option v-for="c in grp.codes" :key="c.code" :value="c.code">
                    {{ c.code }} — {{ c.display }}{{ c.requires_preauth ? ' (preauth required)' : '' }} · KSh {{ c.tariff_kes }}
                  </option>
                </optgroup>
              </select>
              <div class="hms-muted-sm mt-1" v-if="newPre.intervention_code">
                <span class="hms-mono">{{ newPre.intervention_code }}</span> · {{ newPre.intervention_name }}
              </div>
            </div>
            <div class="col-md-4"><label class="hms-label">Requested Amount (KSh) *</label>
              <input type="number" class="hms-input" v-model.number="newPre.requested_amount" />
            </div>
            <div class="col-md-4"><label class="hms-label">ICD-11 Code *</label>
              <input class="hms-input" v-model="newPre.icd11_code" placeholder="DA42.Z" />
            </div>
            <div class="col-md-4"><label class="hms-label">ICD-11 Display</label>
              <input class="hms-input" v-model="newPre.icd11_display" placeholder="Gastritis, unspecified" />
            </div>
            <div class="col-12"><label class="hms-label">Clinical Justification</label>
              <textarea class="hms-input" rows="3" v-model="newPre.clinical_justification" placeholder="Why this service / procedure is medically necessary..."></textarea>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="submitCreate" :disabled="processing || !canCreate">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>Create Draft
          </button>
        </div>
      </div>

      <!-- ============ DETAIL MODAL ============ -->
      <div class="hms-modal-overlay" v-if="detail" @click.self="detail=null">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span>
              <i class="mdi mdi-clipboard-check-outline me-1"></i>{{ detail.preauth_number }}
              <span class="hms-badge ms-2" :class="statusBadge(detail.status)">{{ formatStatus(detail.status) }}</span>
            </span>
            <button class="hms-modal-close" @click="detail=null">&times;</button>
          </div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Patient</span><span><strong>{{ detail.patient_name }}</strong> · {{ detail.patient_no }}</span></div>
            <div><span class="hms-kv-key">SHA Number</span><span class="hms-mono">{{ detail.sha_number || detail.insurance_number || '—' }}</span></div>
            <div><span class="hms-kv-key">Service</span><span><strong>{{ detail.intervention_name }}</strong> <span class="hms-mono">{{ detail.intervention_code }}</span></span></div>
            <div><span class="hms-kv-key">Diagnosis (ICD-11)</span><span>{{ detail.icd11_display }} <span class="hms-mono">{{ detail.icd11_code }}</span></span></div>
            <div><span class="hms-kv-key">Requested</span><span><strong>KSh {{ formatNum(detail.requested_amount) }}</strong></span></div>
            <div v-if="detail.approved_amount > 0"><span class="hms-kv-key">Approved</span><span class="text-success"><strong>KSh {{ formatNum(detail.approved_amount) }}</strong></span></div>
            <div v-if="detail.preauth_token"><span class="hms-kv-key">SHA Pre-Auth Token</span><span class="hms-mono"><strong>{{ detail.preauth_token }}</strong></span></div>
            <div v-if="detail.mediator_id"><span class="hms-kv-key">Mediator ID</span><span class="hms-mono">{{ detail.mediator_id }}</span></div>
            <div v-if="detail.claim_uuid"><span class="hms-kv-key">Claim UUID</span><span class="hms-mono">{{ detail.claim_uuid }}</span></div>
            <div v-if="detail.submitted_date"><span class="hms-kv-key">Submitted</span><span>{{ formatDateTime(detail.submitted_date) }}</span></div>
            <div v-if="detail.decided_date"><span class="hms-kv-key">Decided</span><span>{{ formatDateTime(detail.decided_date) }}</span></div>
            <div v-if="detail.rejection_reason"><span class="hms-kv-key">Reason / Notes</span><span class="hms-pre-text">{{ detail.rejection_reason }}</span></div>
            <div v-if="detail.clinical_justification"><span class="hms-kv-key">Justification</span><span class="hms-pre-text">{{ detail.clinical_justification }}</span></div>
          </div>

          <div class="d-flex gap-2 flex-wrap">
            <button class="hms-btn hms-btn-blue" @click="previewBundle"><i class="mdi mdi-eye-outline me-1"></i>Preview FHIR Bundle</button>
            <template v-if="detail.status === 'draft'">
              <button class="hms-btn hms-btn-primary" @click="submitToSha(false)"><i class="mdi mdi-send me-1"></i>Submit to SHA</button>
            </template>
            <template v-if="['submitted','queued','in_review','clinical_review'].includes(detail.status)">
              <button class="hms-btn hms-btn-primary" @click="pollNow"><i class="mdi mdi-refresh me-1"></i>Poll Status</button>
              <button class="hms-btn hms-btn-blue" @click="submitToSha(false)"><i class="mdi mdi-send me-1"></i>Re-submit</button>
            </template>
          </div>

          <div v-if="bundlePreview" class="hms-pre-json mt-3" style="max-height:320px; overflow:auto;">{{ JSON.stringify(bundlePreview, null, 2) }}</div>
          <div v-if="lastResponse" class="hms-pre-json mt-3" style="max-height:240px; overflow:auto;">{{ lastResponse }}</div>
        </div>
      </div>

      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] { --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;--hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;--hms-br:#2a2a2a;--hms-input-br:#333333;--hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8; }
html[data-theme="light"] { --hms-h:#1a1a1a;--hms-s:#555;--hms-m:#777;--hms-bg1:#fff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#fff;--hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8; }
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-env-pill { padding: 3px 12px; border-radius: 50px; font-size: 11px; font-weight: 700; letter-spacing: 1px; }
.hms-env-pill.env-uat   { background: rgba(255,193,7,.18); color: #ca8a04; }
.hms-env-pill.env-prod  { background: rgba(40,167,69,.15); color: var(--hms-success); }

.hms-muted    { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.text-success { color: var(--hms-success); }
.text-danger { color: var(--hms-danger); }
.text-muted { color: var(--hms-m); }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; }
.hms-card-title { color: var(--hms-h); font-weight: 700; font-size: 14px; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 1px; }
.hms-card-title.small { text-transform: none; letter-spacing: 0; font-size: 13px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 6px 12px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); }
.hms-row-click { cursor: pointer; }
.hms-row-click:hover td { background: var(--hms-primary-lt); }

.hms-badge { padding: 2px 10px; border-radius: 50px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-danger  { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #ca8a04; }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 760px; max-height: calc(100vh - 80px); overflow-y: auto; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }

.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; gap: 8px; }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 150px; font-weight: 600; }
.hms-pre-text { white-space: pre-wrap; }
.hms-pre-json { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; padding: 12px; font-family: ui-monospace; font-size: 11px; white-space: pre-wrap; max-height: 320px; overflow: auto; }
.hms-kv-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.hms-kv-table th { text-align: left; color: var(--hms-s); padding: 6px 10px; vertical-align: top; width: 160px; }
.hms-kv-table td { padding: 6px 10px; }
.hms-kv-table code { background: var(--hms-bg3); padding: 1px 6px; border-radius: 4px; font-size: 12px; }

.hms-search-results { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; margin-top: 6px; max-height: 200px; overflow-y: auto; }
.hms-search-result { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); }
.hms-search-result:hover { background: var(--hms-primary-lt); }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-result { padding: 10px 14px; border-radius: 8px; font-size: 13px; display: flex; flex-direction: column; gap: 4px; }
.hms-result.ok  { background: rgba(40,167,69,.08); color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }
.hms-result.err { background: rgba(220,53,69,.08); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "ShaIntegration",
  components: { Navigation },
  data() {
    return {
      loading: true, processing: false, verifying: '',
      tab: 'preauth',
      env: 'uat', apiBase: '', hospitalId: '',
      preauths: [], preauthStatus: '',
      codes: [], selectedCode: '',
      detail: null, bundlePreview: null, lastResponse: '',

      showCreate: false, createError: '',
      newPre: { patient_id: '', visit_id: '', intervention_code: '', intervention_name: '', icd11_code: '', icd11_display: '', requested_amount: 0, clinical_justification: '' },
      pickedLabel: '',

      patientSearch: '', memberSearch: '',
      patientResults: [], _searchTimer: null,
      memberPatient: null,

      // verify panels
      verifyFid: '', verifyPuid: '', verifyShaNo: '', verifyCrId: '',
      facResult: null, pracResult: null, memResult: null,

      toastMsg: '', toastError: false, toastTimer: null
    };
  },
  computed: {
    canCreate() { return this.newPre.patient_id && this.newPre.intervention_code && this.newPre.icd11_code && this.newPre.requested_amount > 0; },
    groupedCodes() {
      var by = {};
      (this.codes || []).forEach(c => { (by[c.category] = by[c.category] || []).push(c); });
      return Object.keys(by).map(k => ({ cat: k, codes: by[k] }));
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    var t = localStorage.getItem("hms_theme") || "light";
    document.documentElement.setAttribute("data-theme", t);
    this.apiBase    = (process.env.VUE_APP_BASE_URL_HMS || '') + '/index.php';
    this.hospitalId = sessionStorage.getItem("hospitalId") || '';
    this.loadCodes();
    this.loadPreauths();
    this.loadEnv();
  },
  beforeUnmount() { if (this.toastTimer) clearTimeout(this.toastTimer); if (this._searchTimer) clearTimeout(this._searchTimer); },
  methods: {
    showToast(msg, isErr) {
      this.toastMsg = msg; this.toastError = !!isErr;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var s = this; this.toastTimer = setTimeout(() => s.toastMsg = '', 2400);
    },
    formatNum(n) { return n == null ? '0' : Number(n).toLocaleString(); },
    formatDateTime(s) { if (!s) return '—'; var d = new Date(s.replace?s.replace(' ','T'):s); return isNaN(d) ? s : d.toLocaleString(); },
    formatStatus(s) { return ({ draft:'Draft', submitted:'Submitted', queued:'Queued', in_review:'In Review', clinical_review:'Clinical Review', approved:'Approved', rejected:'Rejected', expired:'Expired', used:'Used', cancelled:'Cancelled' })[s] || s; },
    statusBadge(s) { return ({ draft:'hms-badge-neutral', submitted:'hms-badge-info', queued:'hms-badge-info', in_review:'hms-badge-warning', clinical_review:'hms-badge-warning', approved:'hms-badge-success', rejected:'hms-badge-danger', expired:'hms-badge-neutral', used:'hms-badge-purple', cancelled:'hms-badge-neutral' })[s] || 'hms-badge-neutral'; },

    async loadEnv() {
      try {
        var r = await executeJson({ function: "getSystemConfig" }, hms);
        if (r.data && r.data.success) {
          var rows = (r.data.data && r.data.data.Config) || [];
          var envRow = rows.find(c => c.config_key === 'sha_environment');
          if (envRow) this.env = envRow.config_value || 'uat';
        }
      } catch(e) {}
    },
    async loadCodes() {
      try {
        var r = await executeJson({ function: "getShaInterventionCodes" }, hms);
        if (r.data && r.data.success) this.codes = (r.data.data && r.data.data.Codes) || [];
      } catch(e) {}
    },
    async loadPreauths() {
      this.loading = true;
      try {
        var p = { function: "getShaPreauths" };
        if (this.preauthStatus) p.status = this.preauthStatus;
        var r = await executeJson(p, hms);
        if (r.data && r.data.success) this.preauths = (r.data.data && r.data.data.Preauths) || [];
      } catch(e) {}
      this.loading = false;
    },

    debouncedPatientSearch() {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      var s = this; var q = (this.tab === 'verify' ? this.memberSearch : this.patientSearch);
      this._searchTimer = setTimeout(async () => {
        if (!q || q.length < 2) { s.patientResults = []; return; }
        try {
          var r = await executeJson({ function: "getPatients", search: q }, hms);
          if (r.data && r.data.success) s.patientResults = ((r.data.data && r.data.data.Patients) || []).slice(0, 8);
        } catch(e) {}
      }, 300);
    },
    pickPatient(p) {
      this.newPre.patient_id = p.patient_id;
      this.pickedLabel = p.first_name + ' ' + p.last_name + ' (' + p.patient_no + ')';
      this.patientResults = []; this.patientSearch = '';
    },
    pickMember(p) {
      this.memberPatient = p; this.verifyShaNo = p.sha_number || ''; this.verifyCrId = p.cr_id || '';
      this.patientResults = []; this.memberSearch = p.first_name + ' ' + p.last_name;
    },

    onCodeChange() {
      var c = this.codes.find(x => x.code === this.selectedCode);
      if (c) {
        this.newPre.intervention_code = c.code;
        this.newPre.intervention_name = c.display;
        if (!this.newPre.requested_amount) this.newPre.requested_amount = parseFloat(c.tariff_kes);
      }
    },

    openCreate() { this.createError = ''; this.showCreate = true; this.newPre = { patient_id: '', visit_id: '', intervention_code: '', intervention_name: '', icd11_code: '', icd11_display: '', requested_amount: 0, clinical_justification: '' }; this.selectedCode = ''; this.pickedLabel = ''; },
    async submitCreate() {
      this.createError = ''; this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "createShaPreauth" }, this.newPre), hms);
        if (r.data && r.data.success) {
          this.showToast("Pre-auth drafted"); this.showCreate = false;
          await this.loadPreauths();
          await this.openDetail(r.data.data.preauth_id);
        } else this.createError = r.data.message || "Failed";
      } catch(e) { this.createError = "Connection error"; }
      this.processing = false;
    },

    async openDetail(id) {
      this.bundlePreview = null; this.lastResponse = '';
      try {
        var r = await executeJson({ function: "getShaPreauthDetail", preauth_id: id }, hms);
        if (r.data && r.data.success) this.detail = (r.data.data && r.data.data.Preauth) || null;
      } catch(e) {}
    },
    async previewBundle() {
      try {
        var r = await executeJson({ function: "submitShaPreauth", preauth_id: this.detail.preauth_id, dry_run: 1 }, hms);
        if (r.data && r.data.success) this.bundlePreview = (r.data.data && r.data.data.bundle) || null;
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
    },
    async submitToSha() {
      try {
        var r = await executeJson({ function: "submitShaPreauth", preauth_id: this.detail.preauth_id }, hms);
        if (r.data && r.data.success) {
          this.showToast(r.data.message || 'Submitted');
          if (r.data.data && r.data.data.bundle) this.bundlePreview = r.data.data.bundle;
          this.lastResponse = JSON.stringify(r.data.data, null, 2);
          await this.openDetail(this.detail.preauth_id);
          await this.loadPreauths();
        } else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
    },
    async pollNow() {
      try {
        var r = await executeJson({ function: "pollShaPreauth", preauth_id: this.detail.preauth_id }, hms);
        if (r.data && r.data.success) {
          this.showToast('Status: ' + (r.data.data && r.data.data.sha_status || 'unknown'));
          this.lastResponse = JSON.stringify(r.data.data, null, 2);
          await this.openDetail(this.detail.preauth_id); await this.loadPreauths();
        } else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
    },

    async doVerifyFacility() {
      this.verifying = 'fac'; this.facResult = null;
      try {
        var r = await executeJson({ function: "verifyShaFacility", fid: this.verifyFid }, hms);
        this.facResult = { success: !!(r.data && r.data.success), message: (r.data && r.data.message) || '', response: r.data && r.data.data };
      } catch(e) { this.facResult = { success: false, message: 'Connection error' }; }
      this.verifying = '';
    },
    async doVerifyPractitioner() {
      this.verifying = 'prac'; this.pracResult = null;
      try {
        var r = await executeJson({ function: "verifyShaPractitioner", puid: this.verifyPuid }, hms);
        this.pracResult = { success: !!(r.data && r.data.success), message: (r.data && r.data.message) || '', response: r.data && r.data.data };
      } catch(e) { this.pracResult = { success: false, message: 'Connection error' }; }
      this.verifying = '';
    },
    async doVerifyMember() {
      this.verifying = 'mem'; this.memResult = null;
      try {
        var p = { function: "verifyShaMember" };
        if (this.memberPatient) p.patient_id = this.memberPatient.patient_id;
        if (this.verifyShaNo) p.sha_number = this.verifyShaNo;
        if (this.verifyCrId)  p.cr_id = this.verifyCrId;
        var r = await executeJson(p, hms);
        this.memResult = {
          success: !!(r.data && r.data.success),
          message: (r.data && r.data.message) || '',
          sha_number: r.data && r.data.data && r.data.data.sha_number,
          cr_id: r.data && r.data.data && r.data.data.cr_id,
          patient_name: r.data && r.data.data && r.data.data.patient_name
        };
      } catch(e) { this.memResult = { success: false, message: 'Connection error' }; }
      this.verifying = '';
    }
  }
};
</script>
